<script setup lang="ts">
import type { ReadonlyDeep } from 'type-fest';
import type { AddressBookAddressItem } from '@/composables/data/useAddressBookViewData';

import KBtn from '@/components/btns/KBtn.vue';
import ReportGenerateDialogHeader from '@/components/headers/ReportGenerateDialogHeader.vue';
import PageDialogWrapper from '@/components/wrappers/PageDialogWrapper.vue';

export interface Props {
  address?: ReadonlyDeep<AddressBookAddressItem>;
  isConfirmLoading?: boolean;
  confirm?: () => Promise<unknown>;
}
export type DefProps = typeof props;
const props = withDefaults(defineProps<Props>(), {
  address: undefined,
  isConfirmLoading: false,
  confirm: undefined,
});
const emit = defineEmits<{
  cancelBtnClick: [event: Event];
  confirmBtnClick: [event: Event, props: DefProps];
}>();

function handleCancelClick(event: Event): void {
  if (props.isConfirmLoading) return;
  emit('cancelBtnClick', event);
}

function handleConfirmClick(event: Event): void {
  if (props.isConfirmLoading) return;
  emit('confirmBtnClick', event, props);
}
</script>

<template>
  <PageDialogWrapper
    :class="$style.addressBookDeleteAddressDialog"
    data-testid="addressBookDeleteAddressDialog"
  >
    <template #header>
      <ReportGenerateDialogHeader @close-click="handleCancelClick">
        Delete Address
      </ReportGenerateDialogHeader>
    </template>
    <section :class="$style.main">
      <div :class="$style.text">
        Are you sure you want to delete this address<template
          v-if="props.address?.name"
        >
          <strong> "{{ props.address.name }}"</strong></template
        >? This action cannot be undone.
      </div>
    </section>
    <template #footer>
      <menu :class="$style.dialogFooterMenuList">
        <li :class="$style.dialogFooterMenuListItem">
          <KBtn
            variant="outlined"
            append-icon="mdi-close"
            :class="$style.btn"
            @click="handleCancelClick"
          >
            Cancel
          </KBtn>
        </li>
        <li :class="$style.dialogFooterMenuListItem">
          <KBtn
            priority="danger"
            append-icon="mdi-delete"
            :class="$style.btn"
            :loading="props.isConfirmLoading"
            autofocus
            @click="handleConfirmClick"
          >
            Delete
          </KBtn>
        </li>
      </menu>
    </template>
  </PageDialogWrapper>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use 'sass:math';
@use '@/styles/variables';

.addressBookDeleteAddressDialog {
  width: 420px;
}
  .main {
    display: flex;
    flex-direction: column;
    align-items: center;
  }
  .text {
    margin: 0 0 math.div(variables.$pageDialogWindowMainWrapperMargin, 2) 0;
    text-align: center;
  }
  .dialogFooterMenuList {
    list-style: none;
    margin: 0;
    padding: 0;
    text-align: right;
  }
    .dialogFooterMenuListItem {
      display: inline;
    }
    .dialogFooterMenuListItem + .dialogFooterMenuListItem {
      margin-left: 1rem;
    }
</style>
